// A map showing a team or a match (?live=1; teams and matches, Ben 2026-10-30 — docs/modules/
// teams-and-matches.md "6. Shot Map", "7. Pass Map"). The same pitch, chips, table and xG story as a
// player's map (EventMapBody), on the team's events:
// - a team's season (Shot Map; Pass Map from 2026-11-02): a For | Against switch (its own shots or
//   passes, or its opponents');
// - a match dropped on the map (Shot Map, Pass Map): the match, one team at a time, never both on
//   one pitch, with a `Home | Away` switch. The map's player is swapped out (Ben: "If I swap a player
//   with a match, the player is irrelevant"); ✕ on the match chip goes back to what it showed;
// - a player chip ("Declan Rice ✕", Ben): only their events. A game from a player's Matches
//   arrives with it on; click a name in the list to swap it; ✕ for the
//   whole team again. (A player dropped on the map shows their own map, the whole season.) The
//   list's tick boxes show it (only that player ticked); ticking a box turns
//   it into ticks;
// - a players row above the side panel ("Shooters: All ▾", Ben 2026-11-01): a dropdown to pick one
//   or several players. Dragged out, it becomes the players list (Ben's boss's request: most first,
//   a share bar and a tick box each; hover a name to light theirs up), linked to the map like a loose
//   Shot Details (canvas/map-panels.ts); dragged back or closed, it's only the row again.

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { ProtoModuleProps } from "./bridge.js";
import { getModState, setModState } from "./bridge.js";
import { EventMapBody, useRefresh, type EventModuleConfig } from "./EventMapBody.js";
import { useLiveTeamEventMap } from "../hooks/useLiveTeamEventMap.js";
import { shootersOf, type ShotSide } from "../live/team-shots.js";
import { teamOfModule, type TeamRef } from "../live/team.js";
import type { MatchRef } from "../live/match.js";
import { getPlayerShortName } from "../data/player-utils.js";
import { undoApi } from "../canvas/undo.js";
import type { ProtoEvent } from "@catbento/shared";
import { ValuePicker, popoverPosUnder, type PopoverPos } from "./FilterPopovers.js";
import { beginPanelDetach, panelLinkOf, usePanelSlot } from "../canvas/map-panels.js";
import { getDefaultSize } from "./module-config.js";
import { startMenuDrag } from "../canvas/menu-drag.js";

/** The player chip: a player whose events the map is narrowed to. */
export interface MapPlayer {
  id: number;
  name: string;
}

/** The match a map shows (`_mapMatch`), or null. */
export function mapMatchOf(mod: HTMLElement): MatchRef | null {
  return getModState<MatchRef>(mod, "_mapMatch") ?? null;
}

/** Does this map show a team (a team on it, or a match dropped on it)? */
export function showsTeamEvents(mod: HTMLElement): boolean {
  return teamOfModule(mod) !== null || mapMatchOf(mod) !== null;
}

/** A side of a match as a team. */
export function sideTeam(match: MatchRef, side: "home" | "away"): TeamRef {
  const t = match[side];
  return { id: t.id, name: t.name, competitionId: match.competitionId, season: match.season };
}

/** Write a team onto the module and name it in the search box (its player stays underneath). */
function showTeam(mod: HTMLElement, team: TeamRef): void {
  setModState(mod, "_team", team);
  const box = mod.querySelector<HTMLInputElement>(".player-search");
  if (box) box.value = team.name;
}

/** Show a match on a map (Shot Map, Pass Map), the map's player swapped out: `side` (else the team
 *  it already showed, if it played, else the home side), and a player chip if given. ✕ on the match
 *  chip goes back to what the map showed. Holds no Undo step: the caller does. */
export function showMatchOnMap(mod: HTMLElement, match: MatchRef, opts: { side?: "home" | "away"; player?: MapPlayer } = {}): void {
  const shown = teamOfModule(mod);
  const already = shown && (shown.id === match.home.id || shown.id === match.away.id) ? (shown.id === match.home.id ? "home" : "away") : null;
  const side = opts.side ?? already ?? "home";
  // Remember to go back to the player's map, unless it already showed a team or a match.
  if (!shown && !mapMatchOf(mod)) setModState(mod, "_mapFromPlayer", true);
  setModState(mod, "_mapMatch", match);
  setModState(mod, "_mapPlayer", opts.player);
  setModState(mod, "_mapHidden", undefined);
  setModState(mod, "_teamShotSide", undefined);
  showTeam(mod, sideTeam(match, side));
}

export function TeamMap(props: ProtoModuleProps & { cfg: EventModuleConfig }) {
  if (!teamOfModule(props.mod)) throw new Error("TeamMap: no team (a match shows one of its sides)");
  return <TeamEvents {...props} />;
}

function TeamEvents(props: ProtoModuleProps & { cfg: EventModuleConfig }) {
  const { mod, cfg } = props;
  const refresh = useRefresh();
  // Read from the module on every draw: the team switch and the match picker change them and
  // refresh only this component, so values handed down from TeamMap would stay on the old team.
  const match = mapMatchOf(mod);
  const team = teamOfModule(mod);
  if (!team) throw new Error("TeamMap: no team (a match shows one of its sides)");
  const [hover, setHover] = useState<number | null>(null);
  // On a match the switch picks the team, so the events are always that team's own.
  const side: ShotSide = !match && getModState<ShotSide>(mod, "_teamShotSide") === "against" ? "against" : "for";
  const data = useLiveTeamEventMap(mod, team, cfg.key, side, match?.season ?? null, match?.id ?? null);
  const hidden = new Set(getModState<number[]>(mod, "_mapHidden") ?? []);
  const chip = getModState<MapPlayer>(mod, "_mapPlayer") ?? null;
  const seasonEvents = data.state ? data.state.season[cfg.key] : [];

  const change = (fn: () => void) => {
    undoApi().saveState();
    fn();
    setModState(mod, "_mapHidden", undefined);
    refresh();
  };
  const pickMatch = (matchId: number | null) => {
    if (matchId === null) {
      clearMatch();
      return;
    }
    const m = (data.ctx.matchResults[String(team.competitionId)] ?? []).find((f) => f.matchId === matchId);
    if (!m || m.homeTeamId === undefined || m.awayTeamId === undefined || !data.state) return;
    showMatchOnMap(
      mod,
      {
        id: m.matchId,
        competitionId: team.competitionId,
        season: data.state.seasonCode,
        date: m.matchDate.slice(0, 10),
        kickOff: null,
        home: { id: m.homeTeamId, name: m.homeTeamName },
        away: { id: m.awayTeamId, name: m.awayTeamName },
        homeScore: m.homeScore,
        awayScore: m.awayScore,
      },
      { side: m.homeTeamId === team.id ? "home" : "away" },
    );
    refresh();
  };
  const clearMatch = () =>
    change(() => {
      setModState(mod, "_mapMatch", undefined);
      setModState(mod, "_mapPlayer", undefined);
      // Dropped on a player's map: back to the player's map.
      if (getModState<boolean>(mod, "_mapFromPlayer")) {
        setModState(mod, "_mapFromPlayer", undefined);
        setModState(mod, "_team", undefined);
        const box = mod.querySelector<HTMLInputElement>(".player-search");
        const select = mod.querySelector<HTMLSelectElement>(".player-select");
        if (box && select) box.value = select.options[select.selectedIndex]?.text ?? "";
      }
    });
  const setChip = (p: MapPlayer | null) => change(() => setModState(mod, "_mapPlayer", p ?? undefined));

  // The players picked, by name or by tick (Ben, 2026-11-01: "I expect to have a chip appear for the
  // selected players"); ✕ is everyone again.
  const playersChip = (events: readonly ProtoEvent[]) => {
    const list = shootersOf(events);
    const off = offFor(events, hidden, chip?.id ?? null);
    const picked = list.filter((s) => !off.has(s.id));
    if (!chip && (off.size === 0 || picked.length === list.length)) return null;
    const text = chip ? getPlayerShortName({ name: chip.name }) : picked.length === 0 ? "No players" : pickedText(picked);
    return (
      <div className="filter-chip linked-chip">
        <span className="chip-value">{text}</span>
        <span className="remove" onClick={() => setHidden(undefined)}>
          ✕
        </span>
      </div>
    );
  };
  const controls = match ? (
    <>
      <div className="filter-chip linked-chip team-shot-match-chip">
        <span className="chip-value">
          Match: {match.home.name} {match.homeScore}–{match.awayScore} {match.away.name}
        </span>
        <span className="remove" title="Back to what the map showed" onClick={clearMatch}>
          ✕
        </span>
      </div>
      <Switch
        options={[
          { key: String(match.home.id), label: match.home.name },
          { key: String(match.away.id), label: match.away.name },
        ]}
        current={String(team.id)}
        onPick={(key) =>
          change(() => {
            showTeam(mod, sideTeam(match, Number(key) === match.home.id ? "home" : "away"));
            // The other team's events don't have this player in them.
            setModState(mod, "_mapPlayer", undefined);
          })
        }
      />
    </>
  ) : (
    <>
      <Switch
        options={[
          { key: "for", label: "For" },
          { key: "against", label: "Against" },
        ]}
        current={side}
        onPick={(key) => change(() => setModState(mod, "_teamShotSide", key === "against" ? "against" : undefined))}
      />
    </>
  );

  const hoverIds = hover === null ? null : new Set(seasonEvents.filter((s) => s.player?.id === hover).map((s) => s.id));
  // The tick boxes and the player chip are one choice (Ben, 2026-11-01: a name clicked showed their
  // passes but the boxes stayed ticked): a chip shows as only that player ticked, and a box ticked
  // or the dropdown used turns the chip into ticks.
  const setHidden = (ids: number[] | undefined) => {
    undoApi().saveState();
    setModState(mod, "_mapPlayer", undefined);
    setModState(mod, "_mapHidden", ids?.length ? ids : undefined);
    refresh();
  };
  const who = cfg.key === "shots" ? "Shooters" : "Passers";

  return (
    <EventMapBody
      {...props}
      shortName={chip ? getPlayerShortName({ name: chip.name }) : side === "against" ? `${team.name}'s opponents` : team.name}
      data={data}
      refresh={refresh}
      team={{
        controls,
        matchId: match?.id ?? null,
        onPickMatch: pickMatch,
        hiddenShooters: hidden,
        playersChip,
        onlyPlayer: chip?.id ?? null,
        hoverIds,
        side: (events) => (
          <PlayersRow mod={mod} title={who} events={events} hidden={offFor(events, hidden, chip?.id ?? null)} onSet={setHidden}>
            <Players
              events={events}
              hidden={offFor(events, hidden, chip?.id ?? null)}
              chip={chip?.id ?? null}
              onSet={setHidden}
              onHover={setHover}
              onPick={(p) => setChip(chip?.id === p.id ? null : p)}
            />
          </PlayersRow>
        ),
      }}
    />
  );
}

/** "Saka, Havertz", or "Saka, Havertz +2" past two. Pure. */
function pickedText(picked: readonly { name: string }[]): string {
  const two = picked.slice(0, 2).map((s) => s.name).join(", ");
  return picked.length <= 2 ? two : `${two} +${picked.length - 2}`;
}

/** Who's unticked: everyone but the chip's player while there's a chip, else the stored ticks. Pure. */
function offFor(events: readonly ProtoEvent[], hidden: ReadonlySet<number>, chip: number | null): ReadonlySet<number> {
  if (chip === null) return hidden;
  return new Set(shootersOf(events).flatMap((s) => (s.id === chip ? [] : [s.id])));
}

function Switch({ options, current, onPick }: { options: { key: string; label: string }[]; current: string; onPick: (key: string) => void }) {
  return (
    <div className="team-shot-switch" role="group">
      {options.map((o) => (
        <button key={o.key} type="button" className={o.key === current ? "on" : ""} onClick={() => o.key !== current && onPick(o.key)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** "Shooters: All ▾" above the side panel: a press opens a dropdown to pick one or several players
 *  (none picked = all); pressed and moved, it drags out as the players list (`children`), which then
 *  draws in its own module and the row leaves the map (Ben, 2026-11-01: the map "just becomes a
 *  large pitch"). */
function PlayersRow({
  mod,
  title,
  events,
  hidden,
  onSet,
  children,
}: {
  mod: HTMLElement;
  title: string;
  events: readonly ProtoEvent[];
  hidden: ReadonlySet<number>;
  onSet: (hidden: number[] | undefined) => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState<PopoverPos | null>(null);
  const row = useRef<HTMLDivElement>(null);
  // A press on the row while the dropdown is open closes it (outside press) before the click
  // lands; without this the click would open it again at once.
  const closedAt = useRef(0);
  const slot = usePanelSlot(panelLinkOf(mod, "players"));
  const list = shootersOf(events);
  // Each option reads "Bukayo Saka (68)"; the label leads back to the player.
  const label = (s: (typeof list)[number]) => `${s.name} (${s.shots})`;
  const idOf = new Map(list.map((s) => [label(s), s.id]));
  const picked = hidden.size === 0 ? [] : list.filter((s) => !hidden.has(s.id));
  const text = picked.length === 0 ? "All" : pickedText(picked);
  const commit = (values: string[]) => {
    const ids = new Set(values.map((v) => idOf.get(v)).filter((id): id is number => id !== undefined));
    onSet(ids.size === 0 || ids.size === list.length ? undefined : list.filter((s) => !ids.has(s.id)).map((s) => s.id));
  };
  // Out on the canvas: only the list, drawn in its module.
  if (slot) return createPortal(children, slot);
  const onPress = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (window.innerWidth <= 768 || e.button !== 0) return;
    e.stopPropagation();
    const x0 = e.clientX;
    const y0 = e.clientY;
    const onMove = (ev: PointerEvent) => {
      if (Math.hypot(ev.clientX - x0, ev.clientY - y0) < 5) return;
      done();
      const { title: panelTitle, link } = beginPanelDetach(mod, "players");
      const { cols, rows } = getDefaultSize(panelTitle);
      startMenuDrag(ev, panelTitle, cols, rows, { _panelLink: link });
    };
    const done = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", done);
      window.removeEventListener("pointercancel", done);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", done);
    window.addEventListener("pointercancel", done);
  };
  return (
    <>
      <div
        ref={row}
        className="team-players-row"
        onPointerDown={onPress}
        onClick={() => {
          if (Date.now() - closedAt.current < 300) return;
          setOpen(open ? null : row.current ? popoverPosUnder(row.current) : null);
        }}
      >
        <span className="team-players-row-label">{title}:</span>
        <span className="team-players-row-value">{text}</span>
        <span className="team-players-row-caret" aria-hidden="true">▾</span>
        {/* The same pop-out icon as the panel's title: it comes out (Ben, 2026-11-01). */}
        <svg className="event-details-detach" viewBox="0 0 16 16" aria-hidden="true">
          <rect x="2" y="5" width="9" height="9" rx="1.5" />
          <path d="M8 2h6v6M14 2 8.5 7.5" />
        </svg>
      </div>
      {open && (
        <ValuePicker
          label={title}
          pos={open}
          options={list.map(label)}
          activeValue={undefined}
          activeExcluded={false}
          excludable={false}
          selected={picked.map(label)}
          onPick={() => {}}
          onCommit={commit}
          onClose={() => {
            closedAt.current = Date.now();
            setOpen(null);
          }}
        />
      )}
    </>
  );
}

/** `hidden` with `id` ticked or unticked, as the list stores it. Pure. */
function toggled(hidden: ReadonlySet<number>, id: number): number[] | undefined {
  const next = new Set(hidden);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next.size ? [...next] : undefined;
}

/** Every player in the events, most first, a bar for their share and a tick box each. Click a name
 *  with everyone shown for only them (their chip); with a choice already made, a name adds that player
 *  or takes them off, as their box (Ben, 2026-11-01: "if I click the name of a second player, I
 *  expect their check to be added too"); the last one off is everyone again. The top row is everyone at once
 *  (Ben, 2026-11-01: the none / all switch "is quite important and doesn't sit in the correct
 *  place"): its tick box sits in the column of the boxes it sets (ticked = all, empty = none, a dash
 *  = some), and its numbers are the totals of what's ticked, in the columns they add up. The module's
 *  title already says Shooters / Passers. */
function Players({
  events,
  hidden,
  chip,
  onSet,
  onHover,
  onPick,
}: {
  events: readonly ProtoEvent[];
  hidden: ReadonlySet<number>;
  chip: number | null;
  /** The new unticked players (undefined = everyone ticked). */
  onSet: (hidden: number[] | undefined) => void;
  onHover: (id: number | null) => void;
  onPick: (p: MapPlayer) => void;
}) {
  const list = shootersOf(events);
  const on = list.filter((s) => !hidden.has(s.id));
  const shown = on.reduce((t, s) => t + s.shots, 0);
  const goals = on.reduce((t, s) => t + s.goals, 0);
  const most = list[0]?.shots ?? 1;
  const allOn = on.length === list.length;
  const some = on.length > 0 && !allOn;
  // A checkbox's dash ("some") is only settable from script.
  const allBox = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (allBox.current) allBox.current.indeterminate = some;
  }, [some]);
  const pickName = (p: MapPlayer) => {
    // Everyone shown, or their own chip clicked again: the chip (on, or off for everyone).
    if (allOn || chip === p.id) return onPick(p);
    const next = toggled(hidden, p.id);
    onSet(next && next.length === list.length ? undefined : next);
  };
  const nameOf = new Map(events.flatMap((e) => (e.player ? [[e.player.id, e.player.name] as const] : [])));
  // Goals get a column of their own, so every row's bar starts and ends in the same place.
  const withGoals = list.some((s) => s.goals);
  return (
    <div className={`team-shooters${withGoals ? " with-goals" : ""}`} onMouseLeave={() => onHover(null)}>
      <label className={`team-shooter team-shooters-total${on.length === 0 ? " off" : ""}`}>
        <input ref={allBox} type="checkbox" checked={allOn} onChange={() => onSet(allOn ? list.map((s) => s.id) : undefined)} />
        <span className="team-shooter-name">{allOn ? `All ${list.length} players` : `${on.length} of ${list.length} players`}</span>
        <span />
        <span className="team-shooter-count">{shown}</span>
        {withGoals && <span className="team-shooter-goals">{goals ? `(${goals})` : ""}</span>}
      </label>
      {list.map((s) => (
        <div key={s.id} className={`team-shooter${hidden.has(s.id) ? " off" : ""}${chip === s.id ? " chip" : ""}`} onMouseEnter={() => onHover(s.id)}>
          <input type="checkbox" checked={!hidden.has(s.id)} onChange={() => onSet(toggled(hidden, s.id))} />
          <span className="team-shooter-name" onClick={() => pickName({ id: s.id, name: nameOf.get(s.id) ?? s.name })}>
            {s.name}
          </span>
          <span className="team-shooter-bar">
            <span style={{ width: `${(s.shots / most) * 100}%` }} />
          </span>
          <span className="team-shooter-count">{s.shots}</span>
          {withGoals && <span className="team-shooter-goals">{s.goals ? `(${s.goals})` : ""}</span>}
        </div>
      ))}
    </div>
  );
}
